import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import api, { rupiah, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SectionCard, StatusBadge, MetricCard, AlertBanner, EmptyState } from "@/components/ui/design";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Sparkles, ExternalLink, GraduationCap, Trophy } from "lucide-react";

const resultTone = { Correct: "success", "Partially Correct": "warning", "Needs Review": "danger" };

export default function CaseWalkthrough() {
  const [d, setD] = useState(null);
  const [score, setScore] = useState(null);
  const [idx, setIdx] = useState(0);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const loadScore = () => api.get("/case-study/score").then((r) => setScore(r.data)).catch(() => {});
  useEffect(() => { api.get("/case-study/walkthrough").then((r) => setD(r.data)); loadScore(); }, []);
  const item = d?.items?.[idx];
  useEffect(() => {
    if (!item) return;
    setAns(item.answer || {});
    setResult(item.result || null);
    setAi(null);
  }, [idx, d]); // eslint-disable-line

  if (!d) return <Layout><PageHeader title="Walkthrough Studi Kasus" breadcrumb="Pelatihan" /><div className="p-6 text-sm text-muted-foreground">Memuat...</div></Layout>;
  if (!d.seeded) return <Layout><PageHeader title="Walkthrough Studi Kasus" breadcrumb="Pelatihan" /><div className="p-6"><EmptyState icon={GraduationCap} title="Studi kasus belum dibuat" description="Buka halaman Studi Kasus Audit dan tekan 'Buat Studi Kasus' terlebih dahulu." action={<Button onClick={() => nav("/case-study")}>Ke Studi Kasus</Button>} /></div></Layout>;

  const set = (k, v) => setAns((p) => ({ ...p, [k]: v }));
  const open = (tab) => nav(`/engagements/${d.engagement_id}?tab=${tab}`);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/case-study/walkthrough/${item.code}/answer`, ans); setResult(r.data.result); loadScore(); toast.success(`Dinilai: ${r.data.result.label}`); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const askAi = async () => {
    setBusy(true);
    try { const r = await api.post(`/case-study/walkthrough/${item.code}/ai-guidance`, { answer: ans }); setAi(r.data); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Layout>
      <PageHeader breadcrumb="Pelatihan · Studi Kasus" title="Panduan Terpandu (Walkthrough)"
        subtitle="Kerjakan tiap isu langkah demi langkah — tombol aksi membuka catatan Audit OS yang sebenarnya"
        actions={<Button size="sm" variant="outline" onClick={() => nav("/case-study")} data-testid="wt-back-cs">Ringkasan Studi Kasus</Button>} />
      <div className="p-6 grid lg:grid-cols-[1fr_300px] gap-6" data-testid="case-walkthrough-page">
        <div className="space-y-4 animate-in-up">
          {/* stepper */}
          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="wt-prev"><ChevronLeft size={14} />Sebelumnya</Button>
            <div className="flex flex-wrap gap-1 justify-center">
              {d.items.map((it, i) => (
                <button key={it.code} onClick={() => setIdx(i)} data-testid={`wt-step-${it.code}`}
                  className={`w-7 h-7 rounded-md text-[10px] font-semibold border ${i === idx ? "bg-primary text-white border-primary" : it.result ? "bg-green-50 text-green-700 border-green-300" : "bg-white text-muted-foreground border-border"}`}>{i + 1}</button>
              ))}
            </div>
            <Button size="sm" variant="outline" disabled={idx === d.items.length - 1} onClick={() => setIdx(idx + 1)} data-testid="wt-next">Berikutnya<ChevronRight size={14} /></Button>
          </div>

          <SectionCard title={`${item.code} · ${item.title}`} subtitle={`${item.area}${item.amount ? ` · ${rupiah(item.amount)}` : ""}`}
            actions={<StatusBadge tone={item.difficulty === "Tinggi" ? "danger" : item.difficulty === "Sedang" ? "warning" : "neutral"} icon={false}>{item.difficulty}</StatusBadge>}>
            <dl className="space-y-2 text-sm">
              <Row label="Skenario" value={item.scenario} />
              <Row label="Tujuan Audit" value={item.objective} />
              <Row label="Bukti Tersedia" value={item.available_evidence} />
              <Row label="Prosedur yang Direkomendasikan" value={item.recommended_procedure} />
              <Row label="Tindakan yang Diharapkan" value={item.expected_action} />
              <Row label="Fokus Reviewer" value={item.reviewer_focus} />
              <div className="flex flex-wrap gap-1 pt-1">{item.standards.map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 border rounded-md bg-indigo-50 text-indigo-700 border-indigo-200">{s}</span>)}</div>
            </dl>
          </SectionCard>

          {/* action buttons open real records */}
          <SectionCard title="Buka Catatan Audit OS (nyata)">
            <div className="flex flex-wrap gap-2">
              {d.actions.map((a) => (
                <Button key={a.key} size="sm" variant="outline" onClick={() => open(a.tab)} data-testid={`wt-action-${a.key}`}>{a.label}<ExternalLink size={12} className="ml-1" /></Button>
              ))}
            </div>
          </SectionCard>

          {/* answer form */}
          <SectionCard title="Jawaban Trainee (Skor Rule-Based)">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Area Risiko"><Sel v={ans.risk_area} set={(v) => set("risk_area", v)} opts={d.options.risk_areas} tid="wt-risk_area" /></Field>
              <Field label="Asersi"><Sel v={ans.assertion} set={(v) => set("assertion", v)} opts={d.options.assertions} tid="wt-assertion" /></Field>
              <Field label="Jenis Prosedur"><Sel v={ans.procedure} set={(v) => set("procedure", v)} opts={d.options.procedures} tid="wt-procedure" /></Field>
              <Field label="Kesimpulan"><Sel v={ans.conclusion} set={(v) => set("conclusion", v)} opts={d.options.conclusions} tid="wt-conclusion" /></Field>
            </div>
            <div className="flex flex-wrap gap-5 mt-3">
              <Chk label="Bukti memadai" v={ans.evidence_sufficient} set={(v) => set("evidence_sufficient", v)} tid="wt-evidence" />
              <Chk label="Eksepsi teridentifikasi" v={ans.exception} set={(v) => set("exception", v)} tid="wt-exception" />
              <Chk label="Perlu AJE" v={ans.aje} set={(v) => set("aje", v)} tid="wt-aje" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={submit} disabled={busy} data-testid="wt-submit">Nilai Jawaban</Button>
              <Button size="sm" variant="outline" onClick={askAi} disabled={busy} data-testid="wt-ai"><Sparkles size={14} className="mr-1" />Minta Panduan AI</Button>
            </div>

            {result && (
              <div className="mt-4" data-testid="wt-result">
                <AlertBanner tone={resultTone[result.label]} title={`Skor Rule-Based: ${result.label} (${result.correct}/${result.total})`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-1 text-[11px]">
                    {Object.entries(result.detail).map(([k, v]) => (
                      <span key={k} className={v.ok ? "text-green-700" : "text-red-600"}>{v.ok ? "✓" : "✗"} {k}</span>
                    ))}
                  </div>
                </AlertBanner>
              </div>
            )}
            {ai && (
              <div className="mt-3 section-card p-3 bg-indigo-50/40" data-testid="wt-ai-guidance">
                <div className="text-[11px] font-semibold text-indigo-800 mb-1">{ai.disclaimer}</div>
                <p className="text-[13px] whitespace-pre-line">{ai.guidance}</p>
              </div>
            )}
          </SectionCard>
        </div>

        {/* scoring panel */}
        <aside className="space-y-3">
          <SectionCard title="Skor Pelatihan" icon={Trophy}>
            {score && (
              <div className="space-y-3">
                <div className="text-center"><div className="text-4xl font-head font-bold text-navy-700 tabular" data-testid="wt-total-score">{score.total_score}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Skor Total</div></div>
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard label="Dikerjakan" value={`${score.answered}/${score.total_issues}`} />
                  <MetricCard label="Teridentifikasi" value={score.issues_identified} tone="success" />
                  <MetricCard label="Terlewat" value={score.issues_missed} tone={score.issues_missed ? "warning" : "neutral"} />
                  <MetricCard label="Prosedur Benar" value={score.procedures_correct} />
                  <MetricCard label="Bukti Tepat" value={score.evidence_sufficient} />
                  <MetricCard label="Kesimpulan Benar" value={score.conclusions_correct} />
                </div>
                <div className="text-[10px] text-muted-foreground">Skor deterministik & dapat direproduksi. Panduan AI tidak memengaruhi skor.</div>
              </div>
            )}
          </SectionCard>
        </aside>
      </div>
    </Layout>
  );
}

const Row = ({ label, value }) => value ? <div><dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt><dd className="mt-0.5">{value}</dd></div> : null;
const Field = ({ label, children }) => <div><Label className="text-xs">{label}</Label>{children}</div>;
const Sel = ({ v, set, opts, tid }) => (
  <Select value={v || ""} onValueChange={set}><SelectTrigger className="h-8 text-xs" data-testid={tid}><SelectValue placeholder="Pilih" /></SelectTrigger>
    <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
);
const Chk = ({ label, v, set, tid }) => <label className="flex items-center gap-2 text-sm"><Checkbox checked={!!v} onCheckedChange={(x) => set(!!x)} data-testid={tid} />{label}</label>;
